import { useState, useEffect } from 'react'
import { redirectsApi, type Redirect } from '../utils/api'

type Toast = { type: 'success' | 'error'; message: string } | null

export default function Redirects() {
  const [redirects, setRedirects] = useState<Redirect[]>([])
  const [loading, setLoading] = useState(true)
  const [toast, setToast] = useState<Toast>(null)

  // New row form
  const [newSource, setNewSource] = useState('')
  const [newTarget, setNewTarget] = useState('')
  const [adding, setAdding] = useState(false)

  // Inline edit state
  const [editingSource, setEditingSource] = useState<string | null>(null)
  const [editTarget, setEditTarget] = useState('')
  const [saving, setSaving] = useState(false)
  const [deletingSource, setDeletingSource] = useState<string | null>(null)

  function showToast(type: 'success' | 'error', message: string) {
    setToast({ type, message })
    setTimeout(() => setToast(null), 4000)
  }

  async function load() {
    setLoading(true)
    try {
      const { redirects: list } = await redirectsApi.list()
      setRedirects(list)
    } catch (err) {
      showToast('error', `Load failed: ${err instanceof Error ? err.message : ''}`)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault()
    if (!newSource.trim() || !newTarget.trim()) return
    setAdding(true)
    try {
      await redirectsApi.save({ source_url: newSource.trim(), target_url: newTarget.trim() })
      showToast('success', '✓ Redirect added')
      setNewSource('')
      setNewTarget('')
      await load()
    } catch (err) {
      showToast('error', `Failed: ${err instanceof Error ? err.message : ''}`)
    } finally {
      setAdding(false)
    }
  }

  async function handleSaveEdit(source: string) {
    setSaving(true)
    try {
      await redirectsApi.save({ source_url: source, target_url: editTarget.trim() })
      showToast('success', '✓ Redirect updated')
      setEditingSource(null)
      await load()
    } catch (err) {
      showToast('error', `Save failed: ${err instanceof Error ? err.message : ''}`)
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(source: string) {
    if (!confirm(`Delete redirect for "${source}"?`)) return
    setDeletingSource(source)
    try {
      await redirectsApi.delete(source)
      showToast('success', '✓ Redirect deleted')
      await load()
    } catch (err) {
      showToast('error', `Delete failed: ${err instanceof Error ? err.message : ''}`)
    } finally {
      setDeletingSource(null)
    }
  }

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <div>
          <h1 style={{ fontSize: '1.4rem' }}>SEO Redirects</h1>
          <p style={{ fontSize: '0.82rem', marginTop: 4 }}>
            Manage 301 permanent redirects — checked on every page request at the edge
          </p>
        </div>
      </div>

      {/* Add form */}
      <div className="glass-card" style={{ padding: '20px 24px', marginBottom: 20 }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>
          Add Redirect
        </div>
        <form onSubmit={handleAdd} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ flex: 1, minWidth: 200 }}>
            <label className="form-label" htmlFor="input-source-url">Source URL</label>
            <input
              id="input-source-url"
              className="form-input"
              value={newSource}
              onChange={(e) => setNewSource(e.target.value)}
              placeholder="/old-path"
              style={{ fontFamily: 'var(--font-mono)', fontSize: '0.85rem' }}
              required
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', paddingBottom: 1, color: 'var(--accent)', fontSize: '1.2rem' }}>→</div>
          <div className="form-group" style={{ flex: 1, minWidth: 200 }}>
            <label className="form-label" htmlFor="input-target-url">Target URL</label>
            <input
              id="input-target-url"
              className="form-input"
              value={newTarget}
              onChange={(e) => setNewTarget(e.target.value)}
              placeholder="https://toolxdev.org/new-path"
              style={{ fontFamily: 'var(--font-mono)', fontSize: '0.85rem' }}
              required
            />
          </div>
          <button
            id="btn-add-redirect"
            type="submit"
            className="btn btn-primary"
            disabled={adding}
            style={{ flexShrink: 0 }}
          >
            {adding ? '…' : '+ Add'}
          </button>
        </form>
      </div>

      {/* Table */}
      <div className="glass-card" style={{ overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 24 }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="skeleton" style={{ height: 44, marginBottom: 8 }} />
            ))}
          </div>
        ) : redirects.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">↪️</div>
            <p>No redirect rules yet</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Source URL</th>
                <th>Target URL</th>
                <th>Created</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {redirects.map((r) => (
                <tr key={r.source_url}>
                  <td>
                    <code style={{ fontSize: '0.82rem' }}>{r.source_url}</code>
                  </td>
                  <td>
                    {editingSource === r.source_url ? (
                      <input
                        id={`input-edit-target-${r.source_url.replace(/\//g, '-')}`}
                        className="form-input"
                        value={editTarget}
                        onChange={(e) => setEditTarget(e.target.value)}
                        style={{ fontFamily: 'var(--font-mono)', fontSize: '0.82rem', padding: '4px 10px' }}
                        autoFocus
                      />
                    ) : (
                      <code style={{ fontSize: '0.82rem', color: 'var(--accent-hover)' }}>{r.target_url}</code>
                    )}
                  </td>
                  <td>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                      {new Date(r.created_at).toLocaleDateString()}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      {editingSource === r.source_url ? (
                        <>
                          <button
                            id={`btn-save-redirect-${r.source_url.replace(/\//g, '-')}`}
                            className="btn btn-primary btn-sm"
                            onClick={() => handleSaveEdit(r.source_url)}
                            disabled={saving}
                          >
                            {saving ? '…' : '✓ Save'}
                          </button>
                          <button className="btn btn-ghost btn-sm" onClick={() => setEditingSource(null)}>
                            ✕
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            id={`btn-edit-redirect-${r.source_url.replace(/\//g, '-')}`}
                            className="btn btn-ghost btn-sm"
                            onClick={() => { setEditingSource(r.source_url); setEditTarget(r.target_url) }}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            id={`btn-delete-redirect-${r.source_url.replace(/\//g, '-')}`}
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDelete(r.source_url)}
                            disabled={deletingSource === r.source_url}
                          >
                            {deletingSource === r.source_url ? '…' : '🗑'}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {toast && (
        <div className={`toast toast-${toast.type}`} role="alert" aria-live="polite">
          {toast.message}
        </div>
      )}
    </div>
  )
}
